import React, { useEffect, useState, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useLocation } from 'react-router'
import { TabProvider } from '@tecuity/granite-ui/TabProvider'
import { TabPanel } from '@tecuity/granite-ui/TabPanel'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import { DetailCard } from '@tecuity/granite-ui/DetailCard'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import LeftArrowIcon from '@tecuity/sos-icons/react/left-arrow'
import DocumentDeleteIcon from '@tecuity/sos-icons/react/document-delete-solid'
import Pencil from '@tecuity/sos-icons/react/pencil-solid'
import format from 'date-fns/format'
import NotesPanel from '../NotesPanel'
import UccGeneralPanel from './UccGeneralPanel'
import HistoricalDetails from './HistoricalDetails.js'
import './UccRecordDetails.scss'
import { GraniteDataGrid } from '@tecuity/granite-ui/GraniteDataGrid'
import {
  getUccAmendmentByNum,
  getUccAmendmentHistoryList,
  saveUccDocumentNote,
  editUccDocumentNote,
  deleteUccDocumentNote,
} from '../RecordDetailsActions'
import { CLEAR_UCC_DATA, SET_NOTARY_RECORD_DETAILS_SETTINGS } from '../RecordDetailsReducer'
import { Spinner } from 'components/Loaders/Loaders'
import { DetailList } from '@tecuity/granite-ui'
import { checkDLNValid } from '../../BOSearch/BOSearchActions'
import { openSpecificImage } from '../../ImageViewer/ImageViewerActions'
import { setErrorToast } from '../../../actions'
import * as api from '../../../requests.js'
import VoidModal from '../../VoidModal/VoidModal'
import {
  SET_STAFF_FILING_CORRECTION_INFO,
  SET_SELECTED_ROW,
} from '../../WVManager/Workview2Reducer.js'

//recordDetails/UCCSearch/20230000000037
const UccRecordDetails = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const minDate = '0001-01-01T00:00:00'
  const {
    ucc = {},
    uccHistory,
    notaryRecordDetailsSettings,
  } = useSelector((state) => state.record, shallowEqual)
  const { appUserRights = [] } = useSelector((state) => state.auth, shallowEqual)
  const isSidebarCollapsed = useSelector((state) => state.app.sidebarCollapsed)
  const [activeTabId, setActiveTabId] = useState('tab-1')
  const [currentUccNum, setCurrentUccNum] = useState('')
  const [voidModalOpen, setVoidModalOpen] = useState(false)
  const [voidLoading, setVoidLoading] = useState(false)
  const [uccLoading, setUccLoading] = useState(false)
  const tableRef = React.useRef(null)

  const docNum = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location.pathname])

  const handleChange = (tabId) => {
    // console.log(tabId)
    setActiveTabId(tabId)
    if (tabId) {
      window.setTimeout(() => {
        scroll()
      }, 300)
    }
  }

  const scroll = React.useCallback(() => {
    tableRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    return () => {
      dispatch({ type: CLEAR_UCC_DATA })
    }
  }, [])

  useEffect(() => {
    // console.log('docNum', docNum)
    setUccLoading(true)
    dispatch(
      getUccAmendmentByNum(docNum, () => {
        setUccLoading(false)
      })
    )
    api.GET_UCC_FILING_DETAIL_SETTINGS(
      (res) => {
        dispatch({
          type: SET_NOTARY_RECORD_DETAILS_SETTINGS,
          notaryRecordDetailsSettings: res,
        })
      },
      (err) => {
        console.error(err)
      }
    )
  }, [docNum])

  useEffect(() => {
    // console.log('UCC IN STORE: ', ucc)
    if (ucc && ucc.RecordNum) dispatch(getUccAmendmentHistoryList(ucc.RecordNum))
    if (ucc && ucc.FilingNumber) setCurrentUccNum(ucc.FilingNumber)
  }, [ucc])

  // regarding future features and permissions:
  // when buttons for deleting and voiding amendments become features, check for UCC_AMENDMENT_DELETE and UCC AMENDMENT_VOID permissions
  // if the document type is UCC Information Statement rows, the void button will have to check for UCC_INFO_REQUEST_STATEMENT_VOID
  // the NEW FOS button will have to check permissions for UCC_OFFICER_CORRECTION_ADD
  // the Void Dishonored Payment button will have to check permissions for UCC_VOID_DISHONORED_PAYMENT_ADD
  const tableData = useMemo(() => {
    if (!uccHistory.AMENDMENT_LIST) return []
    return uccHistory.AMENDMENT_LIST.map((item, index) => {
      return {
        ...item,
        id: index,
        FilingNumber: item.AMENDMENT_NUM,
        AmendmentType: item.AMENDMENT_TYPE,
        ActionType: item.ACTION_TYPE,
        FilingDate: item.AMENDMENT_DATE,
        ReceiptNumber: item.RECEIPT_NUM,
        ReceiptsTotal: item.RECEIPT_AMOUNT,
        ProcessingStatusType: item.PROCESSING_STATUS,
        LastModifiedBy: item.LAST_MODIFIED_BY,
      }
    })
  }, [uccHistory])

  const template = [
    {
      field: 'FilingNumber',
      flex: 1,
      headerName: 'Filing #',
      disableColumnMenu: true,
    },
    {
      field: 'AmendmentType',
      flex: 1,
      headerName: 'Filing Type',
      disableColumnMenu: true,
    },
    {
      field: 'ActionType',
      flex: 1,
      headerName: 'Action Type',
      disableColumnMenu: true,
    },
    {
      field: 'FilingDate',
      flex: 1,
      headerName: 'Date',
      disableColumnMenu: true,
    },
    {
      field: 'ReceiptNumber',
      flex: 1,
      headerName: 'Receipt #',
      disableColumnMenu: true,
      renderCell: (params) => {
        if (params.row.ReceiptNumber === 'Multiple') return params.row.ReceiptNumber
        return appUserRights.includes('ReceiptView') ? (
          <div
            className="dlnTableLink"
            tabIndex={0}
            onClick={() => {
              history.push(`/accounting/receipt/${params.row.ReceiptNumber}`)
            }}
          >
            {params.row.ReceiptNumber}
          </div>
        ) : (
          params.row.ReceiptNumber
        )
      },
    },
    {
      field: 'ReceiptsTotal',
      flex: 1,
      headerName: 'Receipt Amount',
      disableColumnMenu: true,
      renderCell: (params) => {
        return `$${Number(params.row.ReceiptsTotal)
          .toFixed(2)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
      },
    },
    {
      field: 'ProcessingStatusType',
      flex: 1,
      headerName: 'Filing Status',
      disableColumnMenu: true,
    },
    {
      field: 'LastModifiedBy',
      flex: 1,
      headerName: 'Last Modified By',
      disableColumnMenu: true,
    },
  ]

  const rowDblClick = (params) => {
    checkDLNValid(
      params.row.DLN,
      (id) => {
        dispatch(openSpecificImage(id, params.row.DLN))
      },
      (dln) => {
        dispatch(setErrorToast('DLN ' + dln + ' not found'))
      }
    )
    // dispatch(getUccAmendmentByNum(params.row.FilingNumber))
    setCurrentUccNum(params.row.FilingNumber)
    history.replace(`/recordDetails/UCCSearch/${params.row.FilingNumber}`)
  }

  const saveUccNote = (noteText) => {
    dispatch(saveUccDocumentNote(noteText))
  }

  const editUccNote = (note) => {
    dispatch(editUccDocumentNote(note))
  }

  const deleteUccNote = (noteId) => {
    dispatch(deleteUccDocumentNote(noteId))
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const AddField = (fieldName, value, array) => {
    if (
      notaryRecordDetailsSettings[fieldName] &&
      notaryRecordDetailsSettings[fieldName].Enabled == true
    ) {
      array.push({ label: notaryRecordDetailsSettings[fieldName].DisplayName, value: value })
    }
  }

  const UCCInfoDetails1 = useMemo(() => {
    const details = []
    AddField('UCCRecordType', ucc.UCCRecordType, details)
    AddField('LienType', ucc.LienType, details)
    if (ucc.UCCOtherType && ucc.UCCOtherType !== 'Not Applicable')
      AddField('UCCOtherType', ucc.UCCOtherType, details)
    if (ucc.UCCAltDesignationType && ucc.UCCAltDesignationType !== 'Not Applicable')
      AddField('UCCAltDesignationType', ucc.UCCAltDesignationType, details)
    return details
  }, [ucc, notaryRecordDetailsSettings])

  const UCCInfoDetails2 = useMemo(() => {
    const details = []
    AddField('Status', ucc.Status, details)
    AddField(
      'LapseDate',
      ucc && ucc.LapseDate && ucc.LapseDate !== minDate
        ? format(new Date(ucc.LapseDate), 'MM/dd/yyyy hh:mm a')
        : 'No Lapse Date',
      details
    )

    return details
  }, [ucc, notaryRecordDetailsSettings])

  if (!ucc) {
    return (
      <div style={{ height: '100%', width: '100%', textAlign: 'center', padding: '4rem' }}>
        <Spinner className="page-loader" themeColor="dark-neutral" />
      </div>
    )
  }

  return (
    <TabProvider activeTabId={activeTabId} onChange={(id) => handleChange(id)}>
      <div className="row header-row">
        <h1>Lien Details</h1>
        <DetailCard label="Lien Record Information" className="detailsCard">
          <div className="row">
            <DetailList details={UCCInfoDetails1} topBorder bottomBorder />
            <DetailList details={UCCInfoDetails2} topBorder bottomBorder />
          </div>
        </DetailCard>
        <div className="tablist" role="tablist">
          <TabButton id="tab-1" forPanelId="panel-1" label="General" />
          <TabButton id="tab-3" forPanelId="panel-3" label="Historical Details" />
          <TabButton id="tab-2" forPanelId="panel-2" label="Notes" />
        </div>
      </div>
      <div className="ucc-history">
        <GraniteDataGrid
          rows={tableData}
          columns={template}
          rowHeight={32}
          onRowDoubleClick={rowDblClick}
          pageSize={10}
          rowsPerPageOptions={[10]}
        />
      </div>
      <div ref={tableRef} className="row">
        <TabPanel
          tabId="tab-1"
          id="panel-1"
          children={<UccGeneralPanel currentUccNum={currentUccNum} />}
        />
        <TabPanel tabId="tab-3" id="panel-3">
          <HistoricalDetails />
        </TabPanel>
        <TabPanel
          tabId="tab-2"
          id="panel-2"
          children={
            <NotesPanel saveNote={saveUccNote} editNote={editUccNote} deleteNote={deleteUccNote} />
          }
        />
      </div>
      <div style={{ height: '75px' }}></div>
      <div className={`context-bar-wrapper ${isSidebarCollapsed ? 'sidebar-collapsed ' : ' '}`}>
        <ContextBar className="granite-context-bar">
          <ContextButton
            leftIcon={
              <div className="checkmark-wrapper left">
                <LeftArrowIcon />
              </div>
            }
            onClick={() =>
              history.key ? history.goBack() : history.push('/search/back-office/12')
            }
            variant="secondary"
          >
            Back
          </ContextButton>
          {appUserRights.includes('StaffFilingCorrection') &&
            !uccLoading &&
            ucc.EntryType &&
            ucc.EntryType === 'Paper Filed' && (
              <ContextButton
                rightIcon={
                  <div className="checkmark-wrapper right">
                    <Pencil />
                  </div>
                }
                onClick={() => {
                  dispatch({
                    type: SET_SELECTED_ROW,
                    selectedRow: {},
                  })
                  dispatch({
                    type: SET_STAFF_FILING_CORRECTION_INFO,
                    staffFilingCorrectionInfo: ucc,
                  })
                  history.push(
                    `/office/workview/staffCorrection/${ucc.FormDefinitionId}/${ucc.FilingNum}`
                  )
                }}
                variant="secondary"
              >
                Staff Correction
              </ContextButton>
            )}
          {appUserRights.includes('UCCVoid') && (
            <ContextButton
              rightIcon={
                <div className="checkmark-wrapper right">
                  <DocumentDeleteIcon />
                </div>
              }
              onClick={() => {
                setVoidModalOpen(true)
              }}
              variant="danger"
              // disabled
            >
              Void
            </ContextButton>
          )}
          {voidLoading && (
            <div style={{ marginTop: '20px', marginLeft: '20px', marginRight: '20px' }}>
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )}
        </ContextBar>
      </div>
      <VoidModal
        voidModalOpen={voidModalOpen}
        setVoidModalOpen={setVoidModalOpen}
        label={`UCC Filing ${currentUccNum}`}
        voidMethod={(info) => {
          // console.log(info)
          setVoidLoading(true)
          api.VOID_FILING(
            currentUccNum,
            'UCC',
            info,
            () => {
              setVoidLoading(false)
              window.location.reload()
            },
            (error) => {
              dispatch(setErrorToast('Failed to void filing.'))
              setVoidLoading(false)
            }
          )
        }}
      />
    </TabProvider>
  )
}

export default UccRecordDetails
